import { autobind } from 'core-decorators';
import { injectable } from 'inversify';
import { action, observable } from 'mobx';
import { HttpClient } from '@intelingu/core/configs/axios.config';
import {ACTIONS, EVENTS, STATUS, Step} from "react-joyride";
import {UserApi} from "@intelingu/core/api/UserApi";

export enum ConnectionError {
    TIMEOUT_ERROR = 1,
    NETWORK_ERROR
}

export type TourTrigger = 'cardLoaded' | 'vocabularyExpanded' | 'nextPreloaded';

export interface ExtendedStep extends Step {
    index?: number;
    card?: number;
    waitEvents?: TourTrigger[];
    pause?: boolean;
}

@injectable()
export class AppStore {

    @observable connectionError: ConnectionError = null;

    _tourCurrentCardId;

    @observable tourTriggers = [];
    @observable isJoyrideRunning = false;
    @observable tourFinished = false;
    @observable tourDisableScrolling = true;
    @observable tourParentIndex = 0;
    @observable tourIndex = 0;
    @observable _tour: ExtendedStep[];

    @observable tour: ExtendedStep[] = [];

    audioLegRef: HTMLAudioElement;

    audioSuccessRef: HTMLAudioElement;
    audioSuccessFullRef: HTMLAudioElement;

    constructor(
        private httpClient: HttpClient,
        private userApi: UserApi,
    ) {
        httpClient.client.interceptors.response.use(config => config, this.onResponseError);
    }

    async cacheStep() {
        this.userApi.setOption('ig_option_tour_step', this.tourParentIndex.toString());
    }

    async initTour(tutorial: ExtendedStep[]) {
        this._tour = tutorial.map((value, index) => {
            value.index = index;

            return value;
        });
    }

    @action.bound handleJoyrideCallback(e) {
        const { action, index, status, type } = e;

        const step = this.tour[index];

        if (!step) {
            return;
        }

        if (action === ACTIONS.SKIP || (
            step && step.index >= this._tour[this._tour.length - 1].index && type === 'step:after'
        )) {
            this.userApi.setOption('ig_option_tour_step', 'finished');
            this.tourFinished = true;
            return;
        }

        if (this.tourParentIndex !== step.index && status !== 'paused') {
            // this.isJoyrideRunning = false;
            this.tourParentIndex = this.tour[index].index;
            this.cacheStep();
        }

        const nextIndex = index + 1;

        if ((action === ACTIONS.NEXT && type === 'step:after') || action === ACTIONS.CLOSE) {
            if (this.tour.length > nextIndex && this.tour[nextIndex].waitEvents) {
                const waitFor = this.tour[nextIndex].waitEvents;

                if (this.tourTriggers.filter(t => waitFor.includes(t)).length !== waitFor.length) {
                    this.isJoyrideRunning = false;
                    this.tourIndex += 1;
                }
            } else if (this.tour.length <= nextIndex) {
                this.isJoyrideRunning = false;

                return;
            }
        }

        if (action === ACTIONS.UPDATE && type === 'tooltip') {
            if (this.tour.length > nextIndex && this.tour[nextIndex].disableScrolling) {
                this.tourDisableScrolling = true;
            } else if (this.tourDisableScrolling) {
                this.tourDisableScrolling = false;
            }
        }

        if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
            this.tourIndex = index + (action === ACTIONS.PREV ? -1 : 1);
        } else if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
            this.isJoyrideRunning = false;
        }
    }

    @action.bound tourTrigger(cardId: number, trigger: TourTrigger) {
        if (this.tourFinished) {
            return;
        }

        if (this._tourCurrentCardId !== cardId) {
            this._tourCurrentCardId = cardId;
            this.tourTriggers = [];

            this.tour = this._tour.filter(item => item.card === cardId);
            this.tourIndex = 0;
        }

        if (this.tourParentIndex && !this.isJoyrideRunning) {
            const next = this.tour.findIndex(item => item.index > this.tourParentIndex);

            if (next < 0) {
                return;
            }

            this.tourIndex = next;
        }

        if (this.tour && this.tour.length > this.tourIndex) {
            const waitFor = this.tour[this.tourIndex].waitEvents;
            this.tourTriggers.push(trigger);

            if (!this.tour[this.tourIndex].waitEvents || this.tourTriggers.filter(t => waitFor.includes(t)).length === waitFor.length) {
                this.isJoyrideRunning = true;
            }
        }
    }

    /**
     * Handle network & timeout errors
     *
     * @param error
     */
    @autobind onResponseError(error) {
        if (!error.code && !error.response) {
            this.connectionError = ConnectionError.NETWORK_ERROR;
        } else if (error.code === 'ECONNABORTED' || error.response.status === 408) {
            this.connectionError = ConnectionError.TIMEOUT_ERROR;
        }

        return Promise.reject(error);
    }

    @action.bound resetConnectionError() {
        this.connectionError = null;
    }

    resetTour() {
        this._tourCurrentCardId = null;
        this._tour = null;
        this.tour = [];
        this.tourTriggers = [];
        this.tourParentIndex = 0;
        this.tourIndex = 0;
        this.isJoyrideRunning = false;
        this.tourFinished = false;
    }

}
